import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectGoogleDriveConnection, selectUser } from '@/features/auth/store/selectors';
import { deleteBlog } from '../store/blogSlice';
import { blogAPI } from '../api/blogApi';
import type { Category, Blog } from '../types';
import { Button } from '@/components/ui/button';
import { CategoryManager } from './CategoryManager';
import { PermissionManager } from './PermissionManager';
import { GoogleDriveAuth } from '@/features/auth/components/google';
import { DeleteBlogDialog } from './DeleteBlogDialog';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import { Menu, User, Home, Layers, ChevronRight, Trash2, Shield, FileText, Check, AlertCircle, Loader2 } from 'lucide-react';
import { Separator } from '@/components/ui/separator';
import { toast } from 'sonner';

interface FloatingMenuProps {
  blog: Blog;
  categories: Category[];
  isAdmin: boolean;
  selectedCategoryId: number | null;
  onSelectCategory: (id: number | null) => void;
}

export const FloatingMenu = ({
  blog,
  categories,
  isAdmin,
  selectedCategoryId,
  onSelectCategory,
}: FloatingMenuProps) => {
  const [open, setOpen] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPermissionDialog, setShowPermissionDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [ownerDriveConnected, setOwnerDriveConnected] = useState<boolean | null>(null);
  const user = useAppSelector(selectUser);
  const googleDrive = useAppSelector(selectGoogleDriveConnection);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // Check if user is owner (not just admin)
  const isOwner = user && String(blog.owner_id) === String(user.id);

  useEffect(() => {
    if (!user || isOwner) {
      setOwnerDriveConnected(null);
      return;
    }
    let active = true;
    blogAPI.admin.getBlogDriveStatus(blog.id)
      .then((response) => {
        if (active) setOwnerDriveConnected(response.data.connected);
      })
      .catch(() => {
        if (active) setOwnerDriveConnected(false);
      });
    return () => { active = false; };
  }, [blog.id, isOwner, user]);

  const handleDeleteBlog = async () => {
    setIsDeleting(true);
    try {
      await dispatch(deleteBlog(blog.id)).unwrap();
      toast.success('Đã xóa blog thành công');
      setShowDeleteDialog(false);
      setOpen(false);
      navigate('/profile');
    } catch (error: any) {
      toast.error(error?.message || 'Không thể xóa blog');
      setIsDeleting(false);
    }
  };

  return (
    <>
      {/* Floating Button - Modern minimalist design */}
      <button
        onClick={() => setOpen(true)}
        className="fixed bottom-20 right-4 sm:bottom-6 sm:right-6 z-50 group"
        aria-label="Menu"
      >
        <div className="relative">
          {/* Backdrop blur effect */}
          <div className="absolute inset-0 bg-white/80 backdrop-blur-sm rounded-2xl shadow-lg group-hover:shadow-xl transition-all" />
          {/* Icon */}
          <div className="relative px-3 py-2 sm:px-4 sm:py-3 flex items-center gap-1.5 sm:gap-2">
            <Menu className="h-4 w-4 sm:h-5 sm:w-5 text-gray-700" />
            <span className="text-xs sm:text-sm font-medium text-gray-700 hidden xs:inline">Menu</span>
          </div>
        </div>
      </button>

      {/* Side Sheet Menu - Clean modern design */}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-sm p-0 overflow-y-auto"
        >
          {/* Header */}
          <SheetHeader className="px-6 py-5 border-b">
            <SheetTitle className="text-lg font-semibold text-gray-900">
              {blog.title}
            </SheetTitle>
            <SheetDescription className="sr-only">
              Menu điều hướng và quản lý blog
            </SheetDescription>
          </SheetHeader>

          <div className="flex flex-col h-full">
            {/* User Info - Minimal */}
            {user && (
              <>
                <Link
                  to="/profile"
                  onClick={() => setOpen(false)}
                  className="px-6 py-4 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
                      <User className="h-5 w-5 text-white" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {user.displayName || user.email}
                      </p>
                      <p className="text-xs text-gray-500">
                        {String(blog.owner_id) === String(user.id) ? 'Chủ sở hữu' : 'Admin'}
                      </p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-gray-400" />
                  </div>
                </Link>
                <Separator />
              </>
            )}

            {/* Navigation - Clean list */}
            <div className="py-2">
              <Link
                to="/"
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 px-6 py-3 hover:bg-gray-50 transition-colors"
              >
                <Home className="h-5 w-5 text-gray-600" />
                <span className="text-sm font-medium text-gray-700">Trang chủ</span>
              </Link>

              <button
                onClick={() => {
                  onSelectCategory(null);
                  setOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-6 py-3 transition-colors ${
                  selectedCategoryId === null
                    ? 'bg-blue-50 text-blue-700'
                    : 'hover:bg-gray-50 text-gray-700'
                }`}
              >
                <Layers className="h-5 w-5" />
                <span className="text-sm font-medium">Tất cả bài viết</span>
              </button>
            </div>

            <Separator />

            {/* Policy Links */}
            <div className="py-2">
              <div className="px-6 mb-2">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Điều khoản
                </h3>
              </div>
              <Link
                to="/policies/privacy-policy"
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 px-6 py-2 hover:bg-gray-50 transition-colors"
              >
                <FileText className="h-4 w-4 text-gray-500" />
                <span className="text-sm text-gray-600">Chính sách bảo mật</span>
              </Link>
              <Link
                to="/policies/term-of-service"
                onClick={() => setOpen(false)}
                className="flex items-center gap-3 px-6 py-2 hover:bg-gray-50 transition-colors"
              >
                <FileText className="h-4 w-4 text-gray-500" />
                <span className="text-sm text-gray-600">Điều khoản sử dụng</span>
              </Link>
            </div>

            <Separator />

            {/* Google Drive Connection */}
            {user && (
              <>
                <div className="px-6 py-4">
                  {isOwner ? (
                    googleDrive.status === 'refreshing' || googleDrive.status === 'idle' ? null : <GoogleDriveAuth />
                  ) : ownerDriveConnected === null ? (
                    <div className="flex items-center gap-2 text-sm text-gray-500">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Đang kiểm tra Drive của chủ blog...
                    </div>
                  ) : ownerDriveConnected ? (
                    <div className="flex items-center gap-2 text-sm text-green-700">
                      <Check className="h-4 w-4" />
                      Drive của chủ blog đã kết nối
                    </div>
                  ) : (
                    <div className="flex items-center gap-2 text-sm text-amber-700">
                      <AlertCircle className="h-4 w-4" />
                      Chủ blog chưa kết nối Google Drive
                    </div>
                  )}
                </div>
                <Separator />
              </>
            )}

            {/* Categories - Minimal design */}
            <div className="flex-1 py-4">
              <div className="px-6 mb-3">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">
                  Danh mục
                </h3>
              </div>

              <CategoryManager
                blogId={blog.id}
                categories={categories}
                isAdmin={isAdmin}
                selectedCategoryId={selectedCategoryId}
                onSelectCategory={(id) => {
                  onSelectCategory(id);
                  setOpen(false);
                }}
              />
            </div>

            {/* Owner Actions - Only for Owner */}
            {isOwner && (
              <>
                <Separator />
                <div className="px-6 py-4">
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">
                    Quản lý nâng cao
                  </h3>
                  <div className="grid grid-cols-2 gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setShowPermissionDialog(true);
                        setOpen(false);
                      }}
                      className="justify-start"
                    >
                      <Shield className="mr-1 h-4 w-4" />
                      Phân quyền
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => setShowDeleteDialog(true)}
                      className="justify-start"
                    >
                      <Trash2 className="mr-1 h-4 w-4" />
                      Xóa Blog
                    </Button>
                  </div>
                </div>
              </>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Permission Management Dialog */}
      <PermissionManager
        blog={blog}
        open={showPermissionDialog}
        onOpenChange={setShowPermissionDialog}
      />

      {/* Delete Blog Dialog */}
      <DeleteBlogDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        onConfirm={handleDeleteBlog}
        blogTitle={blog.title}
        isDeleting={isDeleting}
      />
    </>
  );
};
